import { useEffect, useMemo, useRef, useState } from 'react';
import { niceMax, niceTicks } from './jobStats';
import styles from './JobsStatsSection.module.scss';

/** Un morceau d'une colonne (une série). */
export interface ColumnSegment {
  id: string;
  label: string;
  value: number;
  color: string;
}

/** Une colonne : un jour, une heure, un niveau... */
export interface ColumnDatum {
  key: string;
  /** Libellé sous l'axe (peut être sauté si les colonnes sont nombreuses). */
  label: string;
  /** Titre de l'infobulle. */
  title: string;
  segments: ColumnSegment[];
  /** Ligne de plus en bas de l'infobulle (ex. joueurs actifs). */
  note?: string;
}

interface ColumnChartProps {
  data: ColumnDatum[];
  formatValue: (n: number) => string;
  /** Format des graduations (par défaut celui des valeurs). */
  formatTick?: (n: number) => string;
  ariaLabel: string;
  height?: number;
  /** Légende (deux séries ou plus) : identité de chaque couleur, jamais la couleur seule. */
  legend?: { id: string; label: string; color: string }[];
  /** Total affiché dans l'infobulle quand il y a plusieurs morceaux. */
  totalLabel?: string;
}

const PAD_LEFT = 52;
const PAD_BOTTOM = 26;
const PAD_TOP = 10;
const MAX_BAR = 24;
const GAP = 2;

/** Rectangle à coins hauts arrondis (bout de la donnée), base carrée sur l'axe. */
function topRounded(x: number, y: number, w: number, h: number, r: number): string {
  const rr = Math.max(0, Math.min(r, w / 2, h));
  return `M${x},${y + h}V${y + rr}Q${x},${y} ${x + rr},${y}H${x + w - rr}Q${x + w},${y} ${x + w},${y + rr}V${y + h}Z`;
}

/**
 * Colonnes (empilées ou non) en SVG : une seule échelle, graduations rondes, trait fin pour la grille, 2 px de
 * vide entre deux morceaux d'une colonne, bout arrondi de 4 px, infobulle au survol de toute la hauteur de la
 * colonne. Les textes gardent les couleurs du thème ; la couleur ne sert qu'aux marques.
 */
export function ColumnChart({ data, formatValue, formatTick, ariaLabel, height = 220, legend, totalLabel = 'Total' }: ColumnChartProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(640);
  const [hover, setHover] = useState<number | null>(null);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => setWidth(Math.max(240, el.getBoundingClientRect().width));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const totals = useMemo(() => data.map((d) => d.segments.reduce((s, x) => s + Math.max(0, x.value), 0)), [data]);
  const max = niceMax(Math.max(0, ...totals));
  const plotW = width - PAD_LEFT - 8;
  const plotH = height - PAD_TOP - PAD_BOTTOM;
  const slot = data.length > 0 ? plotW / data.length : plotW;
  const barW = Math.max(3, Math.min(MAX_BAR, slot * 0.7));
  const ticks = niceTicks(max);
  const labelEvery = Math.max(1, Math.ceil(data.length / Math.max(2, Math.floor(plotW / 56))));
  const tick = formatTick ?? formatValue;

  const hovered = hover !== null ? data[hover] : null;
  const hoveredX = hover !== null ? PAD_LEFT + hover * slot + slot / 2 : 0;

  return (
    <div className={styles.chartBlock}>
      {legend && legend.length > 1 && (
        <ul className={styles.legend}>
          {legend.map((l) => (
            <li key={l.id}><span className={styles.swatch} style={{ background: l.color }} />{l.label}</li>
          ))}
        </ul>
      )}
      <div ref={wrapRef} className={styles.chartWrap} onMouseLeave={() => setHover(null)}>
        <svg width={width} height={height} role="img" aria-label={ariaLabel} className={styles.chartSvg}>
          {ticks.map((t, i) => {
            const y = PAD_TOP + plotH - (t / max) * plotH;
            return (
              <g key={i}>
                <line x1={PAD_LEFT} x2={width - 8} y1={y} y2={y} className={styles.grid} />
                <text x={PAD_LEFT - 8} y={y + 4} textAnchor="end" className={styles.tick}>{tick(t)}</text>
              </g>
            );
          })}
          {data.map((d, i) => {
            const x = PAD_LEFT + i * slot + (slot - barW) / 2;
            let cursor = PAD_TOP + plotH;
            const visible = d.segments.filter((s) => s.value > 0);
            return (
              <g key={d.key} opacity={hover === null || hover === i ? 1 : 0.55}>
                {visible.map((s, k) => {
                  const h = (s.value / max) * plotH;
                  const top = cursor - h;
                  const shrink = k > 0 ? Math.min(GAP, h) : 0;
                  const rectH = Math.max(0, h - shrink);
                  cursor = top;
                  const isTop = k === visible.length - 1;
                  return isTop
                    ? <path key={s.id} d={topRounded(x, top, barW, rectH, 4)} fill={s.color} />
                    : <rect key={s.id} x={x} y={top} width={barW} height={rectH} fill={s.color} />;
                })}
                {i % labelEvery === 0 && (
                  <text x={x + barW / 2} y={height - 8} textAnchor="middle" className={styles.tick}>{d.label}</text>
                )}
                <rect
                  x={PAD_LEFT + i * slot}
                  y={PAD_TOP}
                  width={slot}
                  height={plotH}
                  fill="transparent"
                  onMouseEnter={() => setHover(i)}
                  onFocus={() => setHover(i)}
                  tabIndex={0}
                  aria-label={`${d.title} : ${formatValue(totals[i] ?? 0)}`}
                />
              </g>
            );
          })}
          <line x1={PAD_LEFT} x2={width - 8} y1={PAD_TOP + plotH} y2={PAD_TOP + plotH} className={styles.axis} />
        </svg>
        {hovered && (
          <div
            className={`${styles.tooltip} ${hoveredX > width * 0.66 ? styles.tooltipLeft : ''}`}
            style={{ left: hoveredX }}
          >
            <strong>{hovered.title}</strong>
            {hovered.segments.filter((s) => s.value > 0).slice().reverse().map((s) => (
              <span key={s.id} className={styles.tooltipRow}>
                <span className={styles.swatch} style={{ background: s.color }} />
                <span className={styles.tooltipLabel}>{s.label}</span>
                <span className={styles.tooltipValue}>{formatValue(s.value)}</span>
              </span>
            ))}
            {hovered.segments.filter((s) => s.value > 0).length > 1 && (
              <span className={styles.tooltipTotal}>{totalLabel} : {formatValue(totals[hover ?? 0] ?? 0)}</span>
            )}
            {hovered.segments.every((s) => s.value <= 0) && <span className={styles.tooltipLabel}>Rien</span>}
            {hovered.note && <span className={styles.tooltipNote}>{hovered.note}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

/** Barres horizontales avec libellé et valeur écrits à côté : une ligne par élément, la part en largeur. */
export function BarList({ rows, formatValue }: {
  rows: { id: string; label: string; color: string; value: number; share: number; sub?: string }[];
  formatValue: (n: number) => string;
}) {
  const max = Math.max(0, ...rows.map((r) => r.share));
  return (
    <ul className={styles.barList}>
      {rows.map((r) => (
        <li key={r.id} className={styles.barRow}>
          <span className={styles.barLabel}>
            <span className={styles.swatch} style={{ background: r.color }} />
            <span className={styles.barName}>{r.label}</span>
          </span>
          <span className={styles.barTrack}>
            <span className={styles.barFill} style={{ width: `${max > 0 ? (r.share / max) * 100 : 0}%`, background: r.color }} />
          </span>
          <span className={styles.barValue}>
            {formatValue(r.value)}
            {r.sub && <span className={styles.barSub}>{r.sub}</span>}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** Une part d'un camembert. */
export interface DonutSlice {
  key: string;
  label: string;
  value: number;
  color: string;
  /** Ligne de plus dans la légende (ex. temps de jeu). */
  sub?: string;
}

/** Arc d'anneau entre deux angles (radians, 0 en haut, sens horaire). */
function ringArc(cx: number, cy: number, r: number, inner: number, a0: number, a1: number): string {
  const p = (rad: number, radius: number) => [cx + radius * Math.sin(rad), cy - radius * Math.cos(rad)];
  const large = a1 - a0 > Math.PI ? 1 : 0;
  const [x0, y0] = p(a0, r);
  const [x1, y1] = p(a1, r);
  const [x2, y2] = p(a1, inner);
  const [x3, y3] = p(a0, inner);
  return `M${x0},${y0}A${r},${r} 0 ${large} 1 ${x1},${y1}L${x2},${y2}A${inner},${inner} 0 ${large} 0 ${x3},${y3}Z`;
}

/**
 * Camembert en anneau : chaque part séparée de la suivante par 2 px de la couleur du fond, la valeur totale au centre,
 * et une légende écrite (libellé, valeur, part en pour cent) : la couleur n'est jamais seule à dire de quoi il s'agit.
 */
export function DonutChart({ slices, formatValue, centerValue, centerLabel, ariaLabel, size = 180 }: {
  slices: DonutSlice[];
  formatValue: (n: number) => string;
  centerValue: string;
  centerLabel: string;
  ariaLabel: string;
  size?: number;
}) {
  const [hover, setHover] = useState<string | null>(null);
  const visible = slices.filter((s) => s.value > 0);
  const total = visible.reduce((s, x) => s + x.value, 0);
  const r = size / 2 - 2;
  const inner = r * 0.62;
  let angle = 0;
  const arcs = visible.map((s) => {
    const span = total > 0 ? (s.value / total) * Math.PI * 2 : 0;
    const a0 = angle;
    angle += span;
    return { s, a0, a1: angle, span };
  });
  return (
    <div className={styles.donut}>
      <svg width={size} height={size} role="img" aria-label={ariaLabel} className={styles.donutSvg}>
        {total === 0 && <circle cx={size / 2} cy={size / 2} r={(r + inner) / 2} fill="none" stroke="var(--color-border)" strokeWidth={r - inner} />}
        {arcs.length === 1 && (
          <circle cx={size / 2} cy={size / 2} r={(r + inner) / 2} fill="none" stroke={arcs[0].s.color} strokeWidth={r - inner}
            onMouseEnter={() => setHover(arcs[0].s.key)} onMouseLeave={() => setHover(null)} />
        )}
        {arcs.length > 1 && arcs.map(({ s, a0, a1 }) => (
          <path key={s.key} d={ringArc(size / 2, size / 2, hover === s.key ? r + 1 : r, inner, a0, a1)} fill={s.color}
            stroke="var(--color-surface)" strokeWidth={2} opacity={hover === null || hover === s.key ? 1 : 0.55}
            onMouseEnter={() => setHover(s.key)} onMouseLeave={() => setHover(null)}>
            <title>{`${s.label} : ${formatValue(s.value)} (${total > 0 ? Math.round((s.value * 1000) / total) / 10 : 0} %)`}</title>
          </path>
        ))}
        <text x={size / 2} y={size / 2 - 2} textAnchor="middle" className={styles.donutValue}>{centerValue}</text>
        <text x={size / 2} y={size / 2 + 16} textAnchor="middle" className={styles.donutLabel}>{centerLabel}</text>
      </svg>
      <ul className={styles.donutLegend}>
        {slices.map((s) => (
          <li key={s.key} className={hover === s.key ? styles.donutLegendActive : undefined}
            onMouseEnter={() => setHover(s.key)} onMouseLeave={() => setHover(null)}>
            <span className={styles.swatch} style={{ background: s.color }} />
            <span className={styles.donutName}>{s.label}{s.sub && <span className={styles.barSub}>{s.sub}</span>}</span>
            <span className={styles.donutNum}>{formatValue(s.value)}</span>
            <span className={styles.donutPct}>{total > 0 ? `${(Math.round((s.value * 1000) / total) / 10).toLocaleString('fr-FR')} %` : '0 %'}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
